<template>
  <div>
    <!-- Hero Section -->
    <section class="py-20 bg-gradient-dark">
      <div class="container mx-auto px-6 text-center">
        <h1 class="text-5xl md:text-6xl font-display font-bold text-gold-400 mb-6">
          Our Gallery
        </h1>
      </div>
    </section>

    <!-- Gallery Section -->
    <section class="py-20">
      <div class="container mx-auto px-6">
        <!-- Gallery Filters -->
        <div class="flex flex-wrap justify-center gap-4 mb-12">
          <button 
            @click="activeCategory = 'all'"
            :class="[
              'px-6 py-3 rounded-full font-semibold transition-all duration-300',
              activeCategory === 'all' 
                ? 'bg-gold-600 text-gray-900 shadow-lg shadow-gold-900/30' 
                : 'bg-gray-800 text-gold-300 hover:bg-gray-700 hover:text-gold-200 border border-gold-800'
            ]"
            :disabled="isLoading"
          >
            All Cases
          </button>
          <button 
            v-for="category in categories"
            :key="category.id"
            @click="activeCategory = category.id"
            :class="[
              'px-6 py-3 rounded-full font-semibold transition-all duration-300',
              activeCategory === category.id 
                ? 'bg-gold-600 text-gray-900 shadow-lg shadow-gold-900/30' 
                : 'bg-gray-800 text-gold-300 hover:bg-gray-700 hover:text-gold-200 border border-gold-800',
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            ]"
            :disabled="isLoading"
          >
            {{ category.name }}
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="max-w-7xl mx-auto">
          <div class="text-center py-20">
            <!-- Spinner -->
            <div class="relative w-24 h-24 mx-auto mb-8">
              <div class="absolute inset-0 border-4 border-gold-900/20 rounded-full"></div>
              <div class="absolute inset-0 border-4 border-transparent border-t-gold-500 rounded-full animate-spin"></div>
              <div class="absolute inset-4 border-4 border-gold-900/10 rounded-full"></div>
              <div class="absolute inset-8 border-4 border-transparent border-b-gold-400 rounded-full animate-spin-reverse"></div>
            </div>
            
            <!-- Loading Text -->
            <h3 class="text-2xl font-display font-bold text-gold-400 mb-4 animate-pulse">
              Loading Gallery Images
            </h3>
            <p class="text-gold-300/70 max-w-md mx-auto">
              Loading high-quality images from our portfolio...
            </p>
            
            <!-- Loading Progress (Optional) -->
            <div class="mt-8 max-w-md mx-auto">
              <div class="w-full bg-gold-900/20 rounded-full h-2.5">
                <div class="bg-gradient-to-r from-gold-600 to-gold-400 h-2.5 rounded-full animate-loading-progress"></div>
              </div>
              <p class="text-gold-400/60 text-sm mt-2">
                {{ loadingProgress }}% loaded
              </p>
            </div>
            
            <!-- Skeleton Loader Grid -->
            <div class="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div v-for="n in 6" :key="n" 
                   class="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gold-800/30 animate-pulse">
                <div class="aspect-square bg-gradient-to-br from-gray-800 to-gray-900 rounded-t-2xl"></div>
                <div class="p-6">
                  <div class="h-6 bg-gold-900/30 rounded-full w-3/4 mb-3"></div>
                  <div class="h-4 bg-gray-700 rounded-full w-1/2"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Gallery Items -->
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          <GalleryItem 
            v-for="item in filteredItems"
            :key="item.id"
            :item="item"
          />
        </div>

        <!-- Empty State -->
        <div v-if="!isLoading && filteredItems.length === 0" class="text-center py-20">
          <svg class="w-24 h-24 text-gold-600/50 mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 class="text-2xl font-display font-bold text-gold-300 mb-2">No Gallery Items Found</h3>
          <p class="text-gold-200/70">Try selecting a different category to view our work</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import GalleryItem from '@/components/GalleryItem.vue'

export default {
  name: 'Gallery',
  components: {
    GalleryItem
  },
  data() {
    return {
      isLoading: true,
      loadingProgress: 0,
      activeCategory: 'all',
      categories: [
        { id: 'cast-partial', name: 'Cast Partial' },
        { id: 'complete-acrylic-denture', name: 'Complete Acrylic Denture' },
        { id: 'zirconia-full-contour', name: 'Zirconia full contour' },
        { id: 'indirect-composite', name: 'indirect composite' },
        { id: 'emax-veneers', name: 'Emax Veneers' },
        { id: 'before-and-after', name: 'before and after' },
        { id: 'feldespathic', name: 'Feldespathic (no prep Veneers)' },
        { id: 'full-anatomy-staining', name: 'Full Anatomy Staining' }
      ],
      galleryItems: [
        // Cast Partial
        { 
          id: 1, 
          category: 'cast-partial', 
          title: 'Cast Partial', 
          image: require('@/assets/CastPartial.jpeg'),
          tag: 'Cast Partial'
        },
        
        // Complete Acrylic Denture
        { 
          id: 2, 
          category: 'complete-acrylic-denture', 
          title: 'Complete Acrylic Denture', 
          image: require('@/assets/completeAcrylicDenture.jpeg'),
          tag: 'Complete Acrylic Denture'
        },
        
        // Zirconia full contour
        { 
          id: 3, 
          category: 'zirconia-full-contour', 
          title: 'Zirconia full contour', 
          image: require('@/assets/Zirconiafullcontour.jpeg'),
          tag: 'Zirconia full contour'
        },
        
        // indirect composite
        { 
          id: 4, 
          category: 'indirect-composite', 
          title: 'indirect composite', 
          image: require('@/assets/indirectcomposite.jpeg'),
          tag: 'indirect composite'
        },
        
        // Emax Veneers
        { 
          id: 5, 
          category: 'emax-veneers', 
          title: 'Emax Veneers', 
          image: require('@/assets/EmaxVeneers.jpeg'),
          tag: 'Emax Veneers'
        },
        { 
          id: 6, 
          category: 'emax-veneers', 
          title: 'Emax Veneers', 
          image: require('@/assets/EmaxVeneers1.jpeg'),
          tag: 'Emax Veneers'
        },
        { 
          id: 7, 
          category: 'emax-veneers', 
          title: 'Emax Veneers', 
          image: require('@/assets/EmaxVeneers2.jpeg'),
          tag: 'Emax Veneers'
        },
        { 
          id: 8, 
          category: 'emax-veneers', 
          title: 'Emax Veneers', 
          image: require('@/assets/EmaxVeneers3.jpeg'),
          tag: 'Emax Veneers'
        },
        
        // before and after
        { 
          id: 9, 
          category: 'before-and-after', 
          title: 'before and after', 
          image: require('@/assets/beforeandafter1.jpeg'),
          tag: 'before and after'
        },
        { 
          id: 10, 
          category: 'before-and-after', 
          title: 'before and after', 
          image: require('@/assets/beforeandafter2.jpeg'),
          tag: 'before and after'
        },
        { 
          id: 11, 
          category: 'before-and-after', 
          title: 'before and after', 
          image: require('@/assets/beforeandafter3.jpeg'),
          tag: 'before and after'
        },
        { 
          id: 12, 
          category: 'before-and-after', 
          title: 'before and after', 
          image: require('@/assets/beforeandafter4.jpeg'),
          tag: 'before and after'
        },
        
        // Feldespathic (no prep Veneers)
        { 
          id: 13, 
          category: 'feldespathic', 
          title: 'Feldespathic (no prep Veneers)', 
          image: require('@/assets/Feldespathic(noprepVeneers)1.jpeg'),
          tag: 'Feldespathic (no prep Veneers)'
        },
        { 
          id: 14, 
          category: 'feldespathic', 
          title: 'Feldespathic (no prep Veneers)', 
          image: require('@/assets/Feldespathic(noprepVeneers)2.jpeg'),
          tag: 'Feldespathic (no prep Veneers)'
        },
        { 
          id: 15, 
          category: 'feldespathic', 
          title: 'Feldespathic (no prep Veneers)', 
          image: require('@/assets/Feldespathic(noprepVeneers)3.jpeg'),
          tag: 'Feldespathic (no prep Veneers)'
        },
        { 
          id: 16, 
          category: 'feldespathic', 
          title: 'Feldespathic (no prep Veneers)', 
          image: require('@/assets/Feldespathic(noprepVeneers)4.jpeg'),
          tag: 'Feldespathic (no prep Veneers)'
        },
        { 
          id: 17, 
          category: 'feldespathic', 
          title: 'Feldespathic (no prep Veneers)', 
          image: require('@/assets/Feldespathic(noprepVeneers)5.jpeg'),
          tag: 'Feldespathic (no prep Veneers)'
        },
        
        // Full Anatomy Staining
        { 
          id: 18, 
          category: 'full-anatomy-staining', 
          title: 'Full Anatomy Staining', 
          image: require('@/assets/FullAnatomyStaining1.jpeg'),
          tag: 'Full Anatomy Staining'
        },
        { 
          id: 19, 
          category: 'full-anatomy-staining', 
          title: 'Full Anatomy Staining', 
          image: require('@/assets/FullAnatomyStaining2.jpeg'),
          tag: 'Full Anatomy Staining'
        },
        { 
          id: 20, 
          category: 'full-anatomy-staining', 
          title: 'Full Anatomy Staining', 
          image: require('@/assets/FullAnatomyStaining3.jpeg'),
          tag: 'Full Anatomy Staining'
        },
        { 
          id: 21, 
          category: 'full-anatomy-staining', 
          title: 'Full Anatomy Staining', 
          image: require('@/assets/FullAnatomyStaining4.jpeg'),
          tag: 'Full Anatomy Staining'
        }
      ]
    }
  },
  computed: {
    filteredItems() {
      if (this.activeCategory === 'all') {
        return this.galleryItems
      }
      return this.galleryItems.filter(item => item.category === this.activeCategory)
    }
  },
  mounted() {
    this.simulateImageLoading()
  },
  methods: {
    simulateImageLoading() {
      // Simulate image loading with progress
      const totalImages = this.galleryItems.length
      let loadedImages = 0
      
      const updateProgress = () => {
        loadedImages++
        this.loadingProgress = Math.min(Math.round((loadedImages / totalImages) * 100), 100)
        
        if (loadedImages === totalImages) {
          setTimeout(() => {
            this.isLoading = false
          }, 500) // Small delay after all images are "loaded"
        }
      }
      
      // Simulate preloading images
      this.galleryItems.forEach(item => {
        const img = new Image()
        img.src = item.image
        img.onload = updateProgress
        img.onerror = updateProgress // Continue even if some images fail
      })
    }
  }
}
</script>

<style scoped>
/* Custom animations */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes spin-reverse {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(-360deg); }
}

@keyframes loading-progress {
  0% { width: 0%; }
  50% { width: 70%; }
  100% { width: 100%; }
}

.animate-spin {
  animation: spin 2s linear infinite;
}

.animate-spin-reverse {
  animation: spin-reverse 1.5s linear infinite;
}

.animate-loading-progress {
  animation: loading-progress 3s ease-in-out infinite;
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Skeleton loading animation */
@keyframes skeleton-loading {
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
}

.skeleton {
  background: linear-gradient(90deg, #1f2937 25%, #374151 50%, #1f2937 75%);
  background-size: 200px 100%;
  animation: skeleton-loading 1.5s infinite;
}

/* Disabled button styling */
button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* Smooth transitions for content */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>